// SPDX-FileCopyrightText: 2023-2025 Open Pioneer project (https://github.com/open-pioneer)
// SPDX-License-Identifier: Apache-2.0

import { BoxProps, chakra, SystemStyleObject } from "@chakra-ui/react";
import { createLogger, Resource } from "@open-pioneer/core";
import {
    CommonComponentProps,
    mergeChakraProps,
    useCommonComponentProps
} from "@open-pioneer/react-utils";
import { useReactiveSnapshot } from "@open-pioneer/reactivity";
import { Coordinate } from "ol/coordinate";
import { Extent } from "ol/extent";
import type OlMap from "ol/Map";
import { sourceId } from "open-pioneer:source-info";
import { ReactNode, RefObject, useEffect, useMemo, useRef, useState } from "react";
import { DISPLAY_STATUS, MapModel, MapPadding } from "../model/MapModel";
import { MapModelProps, useMapModelValue } from "./hooks/useMapModel";
import { MapContainerContextProvider, MapContainerContextType } from "./MapContainerContext";
import { OverlaysRenderer } from "./OverlaysRenderer";

const LOG = createLogger(sourceId);

/**
 * @group UI Components and Hooks
 */
export interface MapContainerProps extends CommonComponentProps, MapModelProps {
    /**
     * Sets the map's padding directly.
     * Do not use the view's padding property directly on the OL map.
     *
     * See: https://openlayers.org/en/latest/apidoc/module-ol_View-View.html#padding)
     */
    viewPadding?: MapPadding | undefined;

    /**
     * Behavior performed by the map when the view padding changes.
     *
     * - `none`: Do nothing.
     * - `preserve-center`: Ensures that the center point remains the same by animating the view.
     * - `preserve-extent`: Ensures that the extent remains the same by zooming.
     *
     * @default "preserve-center"
     */
    viewPaddingChangeBehavior?: "none" | "preserve-center" | "preserve-extent";

    children?: ReactNode;

    /**
     * Optional role property.
     *
     * This property is directly applied to the map's container div element.
     *
     * @default "application"
     */
    role?: string;

    /**
     * Optional aria-labelledby property.
     * Do not use together with aria-label.
     *
     * This property is directly applied to the map's container div element.
     */
    "aria-labelledby"?: string;

    /**
     * Optional aria-label property.
     * Do not use together with aria-label.
     *
     * This property is directly applied to the map's container div element.
     */
    "aria-label"?: string;

    /**
     * Arbitrary html properties that will be applied to the map container's _root_ element.
     * This is the element that contains the map container and any UI elements (like map anchors, for example).
     *
     * Use these at your own risk since they may be overwritten by the map container root itself.
     *
     * Use cases: setting custom data attributes, registering custom event handlers, ...
     */
    rootProps?: BoxProps;

    /**
     * Arbitrary html properties that will be applied to the map container's element.
     * This is the element that _renders_ the OpenLayers map.
     *
     * Use these at your own risk since they may be overwritten by the map container itself.
     *
     * Use cases: setting custom data attributes, registering custom event handlers, ...
     */
    containerProps?: BoxProps;
}

/**
 * Displays the map with the given id.
 *
 * There can only be at most one MapContainer for every map.
 *
 * @group UI Components and Hooks
 */
export function MapContainer(props: MapContainerProps) {
    const {
        viewPadding: viewPaddingProp,
        viewPaddingChangeBehavior,
        children,
        role = "application",
        "aria-label": ariaLabel,
        "aria-labelledby": ariaLabelledBy,
        rootProps,
        containerProps,
        ...restProps
    } = props;
    const { containerProps: rootContainerProps } = useCommonComponentProps(
        "map-container-root",
        restProps // hide role, aria label etc from helper
    );
    const mapContainer = useRef<HTMLDivElement>(null);
    const mapAnchorsHost = useRef<HTMLDivElement>(null);
    const map = useMapModelValue(props);
    const viewPadding = useViewPadding(viewPaddingProp);
    const [ready, setReady] = useState(false);

    // Register as renderer for map model
    useMapContainerRegistration(mapContainer, map);

    // Wait for mount to make sure that the map anchors host is available
    useEffect(() => {
        setReady(true);
    }, []);

    const css = useRootCss(viewPadding);
    const mergedRootProps = useMemo(
        () => mergeChakraProps<BoxProps>({ css }, rootContainerProps, rootProps ?? {}),
        [css, rootProps, rootContainerProps]
    );
    const mergedContainerProps = useMemo(
        () =>
            mergeChakraProps<BoxProps>(
                {
                    className: "map-container",
                    role,
                    "aria-label": ariaLabel,
                    "aria-labelledby": ariaLabelledBy,
                    h: "100%",
                    w: "100%",
                    tabIndex: 0
                },
                containerProps ?? {}
            ),
        [role, ariaLabel, ariaLabelledBy, containerProps]
    );
    return (
        <chakra.div {...mergedRootProps}>
            {/* Used by open layers to mount the map. This node receives the keyboard focus when interacting with the map. */}
            <chakra.div ref={mapContainer} {...mergedContainerProps} />

            {/* Contains user widgets (map anchors and raw children). These are separate from the map so they don't interfere with mouse/keyboard events. */}
            <chakra.div ref={mapAnchorsHost} className="map-anchors">
                {ready && map && (
                    <MapContainerReady
                        map={map}
                        // oxlint-disable-next-line @typescript-eslint/no-non-null-assertion react/refs
                        mapAnchorsHost={mapAnchorsHost.current!}
                        viewPadding={viewPadding}
                        viewPaddingChangeBehavior={viewPaddingChangeBehavior}
                    >
                        {children}
                    </MapContainerReady>
                )}
            </chakra.div>
        </chakra.div>
    );
}

/**
 * This inner component is rendered when the map has been loaded.
 *
 * It provides the map instance and additional properties down the component tree.
 */
function MapContainerReady(
    props: {
        map: MapModel;
        mapAnchorsHost: HTMLElement;
        viewPadding: Required<MapPadding>;
    } & Omit<MapContainerProps, "mapId" | "map" | "className">
): ReactNode {
    const {
        map,
        mapAnchorsHost,
        viewPadding,
        viewPaddingChangeBehavior = "preserve-center",
        children
    } = props;

    // Apply view padding
    useSyncViewPadding(viewPadding, viewPaddingChangeBehavior, map);

    const mapContext = useMemo((): MapContainerContextType => {
        return {
            mapAnchorsHost
        };
    }, [mapAnchorsHost]);
    return (
        <MapContainerContextProvider value={mapContext}>
            <OverlaysRenderer map={map} />
            {children}
        </MapContainerContextProvider>
    );
}

/**
 * Registers the map container as the map's renderer.
 * This can only be done once at a time: there cannot be two renderers for the same map model.
 */
function useMapContainerRegistration(
    mapContainer: RefObject<HTMLDivElement | null>,
    map: MapModel
) {
    useEffect(() => {
        // Mount the map into the DOM
        if (mapContainer.current) {
            const resource = registerMapTarget(map, mapContainer.current);
            return () => resource?.destroy();
        }
    }, [mapContainer, map]);
}

/**
 * Custom CSS rules for the root element.
 */
function useRootCss(viewPadding: Required<MapPadding>) {
    return useMemo((): SystemStyleObject => {
        return {
            height: "100%",
            position: "relative",

            // set css variables according to view padding
            "--map-padding-top": `${viewPadding.top}px`,
            "--map-padding-bottom": `${viewPadding.bottom}px`,
            "--map-padding-left": `${viewPadding.left}px`,
            "--map-padding-right": `${viewPadding.right}px`
        };
    }, [viewPadding]);
}

/**
 * Normalizes the view padding property.
 */
function useViewPadding(viewPaddingProp: MapPadding | undefined): Required<MapPadding> {
    return useMemo<Required<MapPadding>>(() => {
        return {
            left: viewPaddingProp?.left ?? 0,
            right: viewPaddingProp?.right ?? 0,
            top: viewPaddingProp?.top ?? 0,
            bottom: viewPaddingProp?.bottom ?? 0
        };
    }, [
        viewPaddingProp?.left,
        viewPaddingProp?.right,
        viewPaddingProp?.top,
        viewPaddingProp?.bottom
    ]);
}

interface TargetViewPoint {
    center: Coordinate;
    extent: Extent | undefined;
}

/**
 * Applies the current view padding to the view.
 */
function useSyncViewPadding(
    viewPadding: Required<MapPadding>,
    viewPaddingChangeBehavior: MapContainerProps["viewPaddingChangeBehavior"],
    map: MapModel
) {
    const mapView = useReactiveSnapshot(() => map.olView, [map]);
    const displayStatus = useReactiveSnapshot(() => map[DISPLAY_STATUS], [map]);

    // Tracks target state for in-progress animations.
    const targetViewPoint = useRef<TargetViewPoint>(undefined);

    useEffect(() => {
        const olMap = map.olMap;
        if (!mapView || displayStatus !== "ready") {
            return;
        }

        const oldPadding = fromOlPadding(mapView.padding);
        const paddingNotChanged = isPaddingEqual(viewPadding, oldPadding);
        if (paddingNotChanged) {
            return;
        }

        let target = targetViewPoint.current;
        if (!target || !mapView.getAnimating()) {
            const currentCenter = mapView.getCenter();
            if (!currentCenter) {
                return;
            }
            targetViewPoint.current = target = {
                center: currentCenter,
                extent: extentIncludingPadding(olMap, oldPadding)
            };
        }
        mapView.padding = toOlPadding(viewPadding);

        const shouldAnimate = map[DISPLAY_STATUS] === "ready";
        switch (viewPaddingChangeBehavior) {
            case "preserve-center": {
                if (shouldAnimate) {
                    mapView.animate({ center: target.center, duration: 300 }, (done) => {
                        if (done) {
                            targetViewPoint.current = undefined;
                        }
                    });
                } else {
                    mapView.setCenter(target.center);
                    targetViewPoint.current = undefined;
                }
                break;
            }
            case "preserve-extent": {
                if (target.extent) {
                    const res = mapView.getResolutionForExtent(target.extent);
                    if (shouldAnimate) {
                        mapView.animate(
                            {
                                center: target.center,
                                resolution: res,
                                duration: 300
                            },
                            (done) => {
                                if (done) {
                                    targetViewPoint.current = undefined;
                                }
                            }
                        );
                    } else {
                        mapView.setCenter(target.center);
                        mapView.setResolution(res);
                        targetViewPoint.current = undefined;
                    }
                }
                break;
            }
            case "none":
        }
    }, [viewPadding, viewPaddingChangeBehavior, map, mapView, displayStatus]);
}

function registerMapTarget(mapModel: MapModel, target: HTMLDivElement): Resource | undefined {
    const mapId = mapModel.id;
    const olMap = mapModel.olMap;
    if (olMap.getTarget()) {
        LOG.error(
            `Failed to display the map: the map already has a target. There may be more than one <MapContainer />.`
        );
        return undefined;
    }

    LOG.isDebug() && LOG.debug(`Setting target of map '${mapId}':`, target);
    if (!("keyboardEventTarget_" in olMap)) {
        throw new Error(
            "Internal error: failed to override keyboard event target. The property is no longer present."
        );
    }

    // oxlint-disable-next-line @typescript-eslint/no-explicit-any
    (olMap as any).keyboardEventTarget_ = target;
    olMap.setTarget(target);

    let unregistered = false;
    return {
        destroy() {
            if (!unregistered) {
                LOG.isDebug() && LOG.debug(`Removing target of map '${mapId}':`, target);
                // oxlint-disable-next-line @typescript-eslint/no-explicit-any
                (olMap as any).keyboardEventTarget_ = undefined;
                olMap.setTarget(undefined);
                unregistered = true;
            }
        }
    };
}

/**
 * Returns the extent visible in the non-padded region of the map.
 */
function extentIncludingPadding(map: OlMap, padding: Required<MapPadding>): Extent | undefined {
    const size = map.getSize();
    if (!size || size.length < 2) {
        return undefined;
    }

    const [width, height] = size as [number, number];
    const bottomLeft = map.getCoordinateFromPixel([padding.left, padding.bottom]);
    const topRight = map.getCoordinateFromPixel([
        Math.max(0, width - padding.right),
        Math.max(0, height - padding.top)
    ]);
    if (!bottomLeft || !topRight) {
        return undefined;
    }

    const [xmin, ymin] = bottomLeft;
    const [xmax, ymax] = topRight;
    return [xmin, ymin, xmax, ymax] as Extent;
}

function fromOlPadding(padding: number[] | undefined): Required<MapPadding> {
    // top, right, bottom, left
    return {
        top: padding?.[0] ?? 0,
        right: padding?.[1] ?? 0,
        bottom: padding?.[2] ?? 0,
        left: padding?.[3] ?? 0
    };
}

function toOlPadding(padding: Required<MapPadding>): number[] {
    // top, right, bottom, left
    const { top, right, bottom, left } = padding;
    return [top, right, bottom, left];
}

function isPaddingEqual(a: Required<MapPadding>, b: Required<MapPadding>): boolean {
    return a.top === b.top && a.right === b.right && a.bottom === b.bottom && a.left === b.left;
}
